import React, { Suspense, useMemo, useRef, useState, useEffect } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { logo } from "../config";
import globeTexUrl from "../assets/globe-texture.jpg";

function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch (e) {
    return false;
  }
}

const atmosphereVertex = `
  varying vec3 vNormal;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const atmosphereFragment = `
  varying vec3 vNormal;
  void main() {
    float intensity = pow(0.62 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0);
    gl_FragColor = vec4(0.83, 0.68, 0.21, 1.0) * intensity;
  }
`;

function GlobeMesh({ reduced, mobile }) {
  const group = useRef();
  const texture = useLoader(THREE.TextureLoader, globeTexUrl);
  useMemo(() => {
    if (texture) {
      texture.wrapS = THREE.RepeatWrapping;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 8;
    }
  }, [texture]);

  useFrame((_, delta) => {
    if (group.current && !reduced) group.current.rotation.y += delta * 0.11;
  });

  return (
    <group>
      {/* Rotating textured globe — gold oceans, black continents & grid (logo match) */}
      <group ref={group} rotation={[0.33, 0, 0]}>
        <mesh>
          <sphereGeometry args={[1.4, 96, 96]} />
          <meshStandardMaterial
            map={texture}
            metalness={0.55}
            roughness={0.42}
            emissive="#3a2c05"
            emissiveIntensity={0.18}
          />
        </mesh>
        {/* subtle gold meridian sheen overlay */}
        <mesh scale={1.002}>
          <sphereGeometry args={[1.4, 40, 40]} />
          <meshStandardMaterial color="#D4AF37" metalness={1} roughness={0.3} wireframe transparent opacity={0.06} />
        </mesh>
      </group>

      {/* Static silver bezel ring facing the camera (logo bezel) */}
      <mesh rotation={[0, 0, 0]}>
        <torusGeometry args={[1.52, 0.05, 32, 180]} />
        <meshStandardMaterial color="#E5E4E2" metalness={1} roughness={0.14} emissive="#8A8D8F" emissiveIntensity={0.12} />
      </mesh>
      {/* Thin outer gold accent ring */}
      <mesh rotation={[0, 0, 0]} scale={1.06}>
        <torusGeometry args={[1.52, 0.008, 24, 180]} />
        <meshStandardMaterial color="#D4AF37" metalness={1} roughness={0.2} />
      </mesh>

      {/* Atmosphere glow */}
      <mesh scale={1.78}>
        <sphereGeometry args={[1, 48, 48]} />
        <shaderMaterial
          vertexShader={atmosphereVertex}
          fragmentShader={atmosphereFragment}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
          transparent
        />
      </mesh>
    </group>
  );
}

function Particles({ count = 600 }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 3 + Math.random() * 5;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count]);

  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.02;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial color="#D4AF37" size={0.03} sizeAttenuation transparent opacity={0.5} />
    </points>
  );
}

function StaticFallback() {
  return (
    <div className="absolute inset-0 flex items-center justify-center" data-testid="globe-fallback">
      <img
        src={logo}
        alt="Arco Orbis2 globe emblem"
        className="w-[70%] max-w-md object-contain opacity-90 animate-[spin_60s_linear_infinite]"
        style={{ filter: "drop-shadow(0 0 60px rgba(212,175,55,0.25))" }}
      />
    </div>
  );
}

export default function Globe({ reduced }) {
  const [supported, setSupported] = useState(true);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    setSupported(hasWebGL());
    setMobile(window.innerWidth < 768);
  }, []);

  if (!supported) return <StaticFallback />;

  return (
    <div className="absolute inset-0" data-testid="globe-canvas">
      <Canvas
        dpr={mobile ? [1, 1.3] : [1, 1.8]}
        camera={{ position: [0, 0, 5], fov: 45 }}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <ambientLight intensity={0.75} />
        <directionalLight position={[5, 3, 5]} intensity={2.2} color="#FFF4D6" />
        <pointLight position={[-5, -2, 3]} intensity={1.6} color="#C0C0C0" />
        <pointLight position={[0, 4, -4]} intensity={1.2} color="#AA8000" />
        <Suspense fallback={null}>
          <GlobeMesh reduced={reduced} mobile={mobile} />
          {!mobile && <Particles count={reduced ? 200 : 550} />}
        </Suspense>
      </Canvas>
    </div>
  );
}
